/**
 * SmartWizard - Componente entry point para invocar qualquer wizard modular.
 *
 * Uso:
 *   <SmartWizard
 *     open={showWizard}
 *     wizardId="setup-completo"  // ou "add-place", "add-professional", "add-service", "add-schedule"
 *     onClose={() => setShowWizard(false)}
 *     onSuccess={() => reloadData()}
 *   />
 *
 * O SmartWizard carrega automaticamente:
 * - A config do wizard (de wizardConfigs.js)
 * - As entidades existentes (da API via GET /api/entities)
 * - O WizardShell com os steps corretos
 */

import { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography, Dialog } from '@mui/material';
import WizardShell from './WizardShell';
import { WIZARD_CONFIGS } from '../../config/wizardConfigs';
import entitiesService from '../../services/entitiesService';

export default function SmartWizard({
  open,
  wizardId,
  onClose,
  onSuccess,
}) {
  const [loading, setLoading] = useState(true);
  const [existingProfessionals, setExistingProfessionals] = useState([]);
  const [existingServices, setExistingServices] = useState([]);
  const [existingPlaces, setExistingPlaces] = useState([]);
  const [existingSchedules, setExistingSchedules] = useState([]);
  const [existingLinks, setExistingLinks] = useState({});

  const config = WIZARD_CONFIGS[wizardId];

  // Carregar entidades existentes quando o wizard abre
  useEffect(() => {
    if (!open) return;

    const loadExisting = async () => {
      setLoading(true);
      try {
        // Endpoint unico retorna TODAS as entidades e vinculos
        const data = await entitiesService.getAll();

        // Entidades core
        const profsList = data?.professionals || [];
        const svcsList = data?.services || [];
        const placesList = data?.places || [];
        const rawSchedules = data?.schedules || [];

        // Enriquecer schedules com professionalId e placeId (via placeProfessionals)
        const placeProfessionalsMap = (data?.placeProfessionals || []).reduce((acc, pp) => {
          acc[pp.id] = { placeId: pp.placeId, professionalId: pp.professionalId };
          return acc;
        }, {});

        const schedulesList = rawSchedules.map(schedule => {
          const ppData = placeProfessionalsMap[schedule.placeProfessionalId];
          return {
            ...schedule,
            placeId: ppData?.placeId || null,
            professionalId: ppData?.professionalId || null,
          };
        });

        setExistingProfessionals(profsList);
        setExistingServices(svcsList);
        setExistingPlaces(placesList);
        setExistingSchedules(schedulesList);

        // Vinculos flat com IDs de link table
        const links = {
          placeProfessionals: (data?.placeProfessionals || []).map(link => ({
            id: link.id,
            place: link.placeId,
            professional: link.professionalId,
          })),
          placeServices: (data?.placeServices || []).map(link => ({
            id: link.id,
            place: link.placeId,
            service: link.serviceId,
          })),
          placeServiceProfessionals: (data?.placeServiceProfessionals || []).map(link => ({
            id: link.id,
            placeServiceId: link.placeServiceId,
            placeProfessionalId: link.placeProfessionalId,
            place: link.placeId,
            service: link.serviceId,
            professional: link.professionalId,
          })),
        };
        setExistingLinks(links);

        console.log('[SmartWizard] Entidades carregadas (GET /api/entities):', {
          professionals: profsList.length,
          services: svcsList.length,
          places: placesList.length,
          schedules: schedulesList.length,
          links: {
            placeProfessionals: links.placeProfessionals.length,
            placeServices: links.placeServices.length,
            placeServiceProfessionals: links.placeServiceProfessionals.length,
          },
        });
      } catch (error) {
        console.error('[SmartWizard] Erro ao carregar entidades:', error);
      } finally {
        setLoading(false);
      }
    };

    loadExisting();
  }, [open]);

  if (!config) {
    console.error(`[SmartWizard] Config nao encontrada para wizardId: "${wizardId}"`);
    return null;
  }

  // Nao renderizar WizardShell ate os dados estarem prontos
  // Isso evita que Dialog.onEnter dispare loadExisting com arrays vazios
  if (loading && open) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Carregando dados...
          </Typography>
        </Box>
      </Dialog>
    );
  }

  return (
    <WizardShell
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      config={config}
      existingProfessionals={existingProfessionals}
      existingServices={existingServices}
      existingPlaces={existingPlaces}
      existingSchedules={existingSchedules}
      existingLinks={existingLinks}
    />
  );
}
